import { createRoute } from '@tanstack/react-router'
import { rootRoute, requireAuth } from './__root'
import {
  useCrons,
  useCronRuns,
  useTriggerCron,
  useToggleCron,
  type CronJob,
  type CronRun,
} from '../api/hooks'
import { useState } from 'react'

export const cronsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crons',
  beforeLoad: requireAuth,
  component: CronsPage,
})

// ─── Helpers ──────────────────────────────────────────────────

function formatRelative(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 0) {
    // future
    const secs = Math.round(-diff / 1000)
    if (secs < 60) return `in ${secs}s`
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `in ${mins}m`
    return `in ${Math.floor(mins / 60)}h`
  }
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function formatDuration(start: string, end?: string): string {
  if (!end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 1000) return `${ms}ms`
  const secs = Math.round(ms / 1000)
  if (secs < 60) return `${secs}s`
  return `${Math.floor(secs / 60)}m ${secs % 60}s`
}

function runStatusColour(status: string): string {
  if (status === 'success' || status === 'completed') return 'bg-green-900/50 text-green-300'
  if (status === 'failed' || status === 'error') return 'bg-red-900/50 text-red-300'
  if (status === 'running') return 'bg-blue-900/50 text-blue-300'
  return 'bg-gray-800 text-gray-400'
}

// ─── Cron Runs Panel ─────────────────────────────────────────

function CronRunsPanel({ id }: { id: string }) {
  const { data, isLoading } = useCronRuns(id)

  if (isLoading) {
    return (
      <div className="px-4 py-3 space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-4 bg-gray-800 rounded animate-pulse" />
        ))}
      </div>
    )
  }

  const runs: CronRun[] = data?.runs ?? []

  if (runs.length === 0) {
    return <p className="px-4 py-3 text-gray-500 text-xs">No runs yet</p>
  }

  return (
    <div className="px-4 py-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-800">
            <th className="text-left pb-2 text-gray-500 font-medium">Status</th>
            <th className="text-left pb-2 text-gray-500 font-medium">Started</th>
            <th className="text-left pb-2 text-gray-500 font-medium">Duration</th>
            <th className="text-left pb-2 text-gray-500 font-medium">Output</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/50">
          {runs.slice(0, 20).map((r) => (
            <tr key={r.id} className="align-top">
              <td className="py-2 pr-3">
                <span className={`px-1.5 py-0.5 rounded font-medium ${runStatusColour(r.status)}`}>
                  {r.status}
                </span>
              </td>
              <td className="py-2 pr-3 text-gray-400">{formatRelative(r.started_at)}</td>
              <td className="py-2 pr-3 text-gray-400">{formatDuration(r.started_at, r.finished_at)}</td>
              <td className="py-2 text-gray-500 font-mono max-w-xs truncate">
                {r.output ? r.output.slice(0, 200) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Cron Row ─────────────────────────────────────────────────

function CronRow({ job }: { job: CronJob }) {
  const [expanded, setExpanded] = useState(false)
  const [triggered, setTriggered] = useState(false)
  const trigger = useTriggerCron()
  const toggle = useToggleCron()

  const handleTrigger = (e: React.MouseEvent) => {
    e.stopPropagation()
    trigger.mutate(job.id, {
      onSuccess: () => {
        setTriggered(true)
        setTimeout(() => setTriggered(false), 2000)
      },
    })
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggle.mutate({ id: job.id, enabled: !job.enabled })
  }

  return (
    <>
      <tr
        className="hover:bg-gray-800/50 transition-colors cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-3 text-gray-200">{job.name}</td>
        <td className="px-4 py-3">
          <code className="text-xs text-gray-400 font-mono">{job.schedule}</code>
        </td>
        <td className="px-4 py-3">
          <button
            onClick={handleToggle}
            disabled={toggle.isPending}
            className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${
              job.enabled
                ? 'bg-green-900/50 text-green-300 hover:bg-green-900/70'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            } disabled:opacity-50`}
          >
            {job.enabled ? 'enabled' : 'disabled'}
          </button>
        </td>
        <td className="px-4 py-3 text-gray-500 text-xs">{formatRelative(job.lastRun)}</td>
        <td className="px-4 py-3 text-gray-500 text-xs">{formatRelative(job.nextRun)}</td>
        <td className="px-4 py-3">
          <button
            onClick={handleTrigger}
            disabled={trigger.isPending}
            className="text-xs px-3 py-1 bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-white rounded transition-colors flex items-center gap-1.5"
          >
            {trigger.isPending ? (
              <>
                <span className="inline-block w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                Running…
              </>
            ) : triggered ? (
              '✓ Queued'
            ) : (
              'Run now'
            )}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/50">
          <td colSpan={6} className="border-b border-gray-800">
            <CronRunsPanel id={job.id} />
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Skeleton rows ────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr key={i}>
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 bg-gray-800 rounded animate-pulse" style={{ width: j === 0 ? '60%' : j === 1 ? '80%' : '50%' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────

function CronsPage() {
  const { data, isLoading, error } = useCrons()
  const crons: CronJob[] = data?.crons ?? []

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Cron Jobs</h1>

      {error && (
        <p className="text-red-400 mb-4">Failed to load: {error.message}</p>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Name</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Schedule</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Enabled</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Last Run</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Next Run</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {isLoading ? (
              <SkeletonRows />
            ) : crons.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No cron jobs configured
                </td>
              </tr>
            ) : (
              crons.map((job) => <CronRow key={job.id} job={job} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
